
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';

export default function FAQsPage() {
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [openFAQ, setOpenFAQ] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const faqCategories = [
    { id: 'general', name: 'General', icon: 'ri-question-line' },
    { id: 'booking', name: 'Booking', icon: 'ri-calendar-line' },
    { id: 'payment', name: 'Payment', icon: 'ri-money-rupee-circle-line' },
    { id: 'service', name: 'Service', icon: 'ri-tools-line' },
    { id: 'partner', name: 'Partners', icon: 'ri-user-star-line' },
    { id: 'refund', name: 'Refunds', icon: 'ri-refund-line' }
  ];

  const faqs = {
    general: [
      {
        id: 'what-is-bnm',
        question: 'What is BNM (Baainearme)?',
        answer: 'BNM is a trusted platform that connects customers with verified service providers for various home services like cleaning, repairs, beauty services, and more. We make it easy to book quality services at your doorstep.'
      },
      {
        id: 'service-areas',
        question: 'In which cities is BNM available?',
        answer: 'Currently, BNM operates in Nagpur and surrounding areas. We are rapidly expanding to other cities in Maharashtra and across India. Stay tuned for updates!'
      },
      {
        id: 'operating-hours',
        question: 'What are your operating hours?',
        answer: 'Our customer support is available 24/7. Service bookings can be made anytime, and most services are available from 8:00 AM to 8:00 PM. Some emergency services are available 24/7.'
      },
      {
        id: 'partner-verification',
        question: 'How do you verify your service partners?',
        answer: 'All our partners go through a rigorous verification process including background checks, skill assessment, document verification (Aadhaar, PAN), and training before they can start accepting jobs.'
      }
    ],
    booking: [
      {
        id: 'how-to-book',
        question: 'How do I book a service?',
        answer: "You can book services through our mobile app or website. Simply select the service you need, choose your preferred time slot, provide your address, and make the payment. You'll receive confirmation immediately."
      },
      {
        id: 'advance-booking',
        question: 'How far in advance can I book a service?',
        answer: 'You can book services up to 30 days in advance. For same-day bookings, we recommend booking at least 2-3 hours ahead for better availability.'
      },
      {
        id: 'modify-booking',
        question: 'Can I modify or cancel my booking?',
        answer: 'Yes, you can modify or cancel your booking up to 2 hours before the scheduled time through the app or by calling our support team. Cancellations within 2 hours may incur a small fee.'
      },
      {
        id: 'multiple-services',
        question: 'Can I book multiple services at once?',
        answer: 'Yes, you can book multiple services in a single order. Our system will optimize the scheduling and may assign different partners based on their expertise and availability.'
      },
      {
        id: 'urgent-booking',
        question: 'Do you provide same-day or urgent services?',
        answer: "Yes, we offer same-day services based on partner availability. For urgent requests, please call our support team at 93074 19845, and we'll do our best to accommodate your needs."
      }
    ],
    payment: [
      {
        id: 'payment-methods',
        question: 'What payment methods do you accept?',
        answer: 'We accept all major payment methods including credit/debit cards, UPI, net banking, and digital wallets. All payments are processed securely through encrypted payment gateways.'
      },
      {
        id: 'advance-payment',
        question: 'Why do I need to pay in advance?',
        answer: 'We require a small advance payment to confirm your booking and ensure commitment from both parties. This helps us maintain service quality and reduces no-shows.'
      },
      {
        id: 'payment-failure',
        question: 'What if my payment fails?',
        answer: "If your payment fails, you can retry immediately. If the amount was deducted but the booking wasn't confirmed, the money will be refunded within 3-5 business days automatically."
      },
      {
        id: 'pricing-transparent',
        question: 'Are there any hidden charges?',
        answer: 'No, we believe in transparent pricing. The price shown during booking is the final price you pay. There are no hidden charges or surge pricing.'
      },
      {
        id: 'payment-receipt',
        question: 'Will I get a payment receipt?',
        answer: "Yes, you'll receive a digital receipt via email and SMS immediately after payment. You can also download receipts from your app account anytime."
      }
    ],
    service: [
      {
        id: 'service-quality',
        question: 'How do you ensure service quality?',
        answer: 'We maintain quality through partner training, customer feedback systems, random quality checks, and a rating system. Partners with consistently low ratings are removed from our platform.'
      },
      {
        id: 'partner-late',
        question: 'What if the service partner is late?',
        answer: "If your partner is running late, you'll receive real-time updates via SMS or app notification. Partners are encouraged to be punctual, and repeated tardiness results in penalties."
      },
      {
        id: 'unsatisfied-service',
        question: "What if I'm not satisfied with the service?",
        answer: "If you're not satisfied, please rate your experience and provide feedback. Based on the issue, we may offer a partial refund, free re-service, or other appropriate resolution."
      },
      {
        id: 'equipment-tools',
        question: 'Do service partners bring their own equipment?',
        answer: 'Yes, most partners bring their own professional tools and equipment. For services requiring special equipment, this will be mentioned in the service description.'
      },
      {
        id: 'insurance-coverage',
        question: 'Are services covered by insurance?',
        answer: "Yes, all our services are covered by comprehensive insurance for damages or accidents during service delivery. However, this doesn't cover pre-existing damages or issues."
      }
    ],
    partner: [
      {
        id: 'become-partner',
        question: 'How can I become a service partner?',
        answer: 'You can apply through our website or visit our Nagpur office. The process includes filling an application, document verification, skill assessment, and app training. Once approved, you can start accepting jobs immediately.'
      },
      {
        id: 'partner-earnings',
        question: 'How much can partners earn?',
        answer: 'Partner earnings vary based on services provided, hours worked, and performance. On average, partners earn ₹25,000-₹50,000 per month. Top performers can earn even more with bonuses and incentives.'
      },
      {
        id: 'partner-payment',
        question: 'When do partners get paid?',
        answer: "Partners receive weekly payouts directly to their registered bank accounts. Payments are processed every Monday for the previous week's completed services."
      },
      {
        id: 'partner-training',
        question: 'Do you provide training to partners?',
        answer: 'Yes, we provide comprehensive training covering service standards, app usage, customer interaction, and safety protocols before partners start working.'
      },
      {
        id: 'partner-support',
        question: 'What support do you provide to partners?',
        answer: 'We provide 24/7 partner support, regular training updates, marketing support, insurance coverage, and a dedicated partner success team to help resolve any issues.'
      }
    ],
    refund: [
      {
        id: 'refund-policy',
        question: 'What is your refund policy?',
        answer: "We offer refunds for cancellations made at least 2 hours before service time, service quality issues, or if we're unable to provide the service. Refunds are processed within 3-5 business days."
      },
      {
        id: 'partial-refund',
        question: 'Do you offer partial refunds?',
        answer: "Yes, we may offer partial refunds in cases where the service was partially completed or if there were minor issues that didn't completely affect the service quality."
      },
      {
        id: 'refund-processing',
        question: 'How long does it take to process refunds?',
        answer: 'Refunds are typically processed within 3-5 business days. The time for the amount to reflect in your account depends on your bank or payment provider.'
      },
      {
        id: 'refund-method',
        question: 'How will I receive my refund?',
        answer: "Refunds are processed back to the original payment method used for booking. If that's not possible, we'll credit the amount to your BNM wallet for future bookings."
      },
      {
        id: 'refund-status',
        question: 'How can I check my refund status?',
        answer: 'You can check your refund status in the app under "My Orders" or by contacting our customer support team with your order ID.'
      }
    ]
  };

  const allFAQs = Object.values(faqs).flat();
  const filteredFAQs = searchQuery
    ? allFAQs.filter(faq =>
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : faqs[selectedCategory] || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      <Navbar />

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-purple-700 text-white relative overflow-hidden"
               style={{
                 backgroundImage: `url('https://readdy.ai/api/search-image?query=person%20reading%20FAQ%20document%20with%20question%20marks%20around%2C%20modern%20help%20center%20with%20purple%20and%20white%20design%2C%20customer%20support%20illustrations%2C%20friendly%20assistance%20concept%2C%20clean%20professional%20setting&width=1200&height=500&seq=faqs-hero&orientation=landscape')`,
                 backgroundSize: 'cover',
                 backgroundPosition: 'center'
               }}>
        <div className="absolute inset-0 bg-purple-600/80"></div>
        <div className="container mx-auto px-6 relative z-10 text-center">
          <h1 className="text-4xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-xl max-w-2xl mx-auto">
            Find quick answers to common questions about BNM services
          </p>
        </div>
      </section>

      {/* Search Bar */}
      <section className="py-8 bg-white shadow-sm">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="ri-search-line text-gray-400 text-sm"></i>
              </div>
              <input
                type="text"
                placeholder="Search for answers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Categories and FAQs */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-4 gap-8">
              {/* Categories Sidebar */}
              <div className="lg:col-span-1">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Categories</h3>
                <div className="space-y-2">
                  {faqCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => {
                        setSelectedCategory(category.id);
                        setSearchQuery('');
                      }}
                      className={`w-full flex items-center px-4 py-3 rounded-lg text-left transition-colors cursor-pointer whitespace-nowrap ${
                        selectedCategory === category.id && !searchQuery
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <i className={`${category.icon} mr-3`}></i>
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* FAQs Content */}
              <div className="lg:col-span-3">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">
                    {searchQuery ? `Search Results (${filteredFAQs.length})` : 
                     faqCategories.find(c => c.id === selectedCategory)?.name + ' FAQs'}
                  </h2>
                </div>

                <div className="space-y-4">
                  {filteredFAQs.map((faq) => (
                    <div key={faq.id} className="bg-white rounded-xl shadow-sm border border-gray-100">
                      <button
                        onClick={() => setOpenFAQ(openFAQ === faq.id ? null : faq.id)}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        <h3 className="font-semibold text-gray-800 pr-4">{faq.question}</h3>
                        <i className={`ri-arrow-down-s-line text-gray-400 transition-transform flex-shrink-0 ${
                          openFAQ === faq.id ? 'rotate-180' : ''
                        }`}></i>
                      </button>
                      {openFAQ === faq.id && (
                        <div className="px-6 pb-4">
                          <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {filteredFAQs.length === 0 && (
                  <div className="text-center py-12">
                    <i className="ri-search-line text-4xl text-gray-300 mb-4"></i>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No results found</h3>
                    <p className="text-gray-500">Try adjusting your search terms or browse different categories</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Still Need Help */}
      <section className="py-16 bg-purple-50">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Still Have Questions?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Can't find what you're looking for? Our support team is here to help
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/customer-support" className="bg-purple-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-purple-700 transition-colors cursor-pointer whitespace-nowrap">
              Contact Support
            </Link>
            <a href="tel:93074 19845" className="bg-white text-purple-600 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap border-2 border-purple-600">
              Call: 93074 19845
            </a>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Quick Links</h2>
            <p className="text-xl text-gray-600">Explore more about BNM services</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <Link to="/book-services" className="bg-purple-50 rounded-xl p-6 text-center hover:bg-purple-100 transition-colors cursor-pointer">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Book Services</h3>
              <p className="text-gray-600">Browse and book from 50+ home services</p>
            </Link>

            <Link to="/partner" className="bg-purple-50 rounded-xl p-6 text-center hover:bg-purple-100 transition-colors cursor-pointer">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-star-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Become Partner</h3>
              <p className="text-gray-600">Join our network and start earning</p>
            </Link>

            <Link to="/customer-support" className="bg-purple-50 rounded-xl p-6 text-center hover:bg-purple-100 transition-colors cursor-pointer">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Get Support</h3>
              <p className="text-gray-600">Need help? Contact our support team</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              {/* BNM logo (SVG) — scalable, color via currentColor */}
<svg
  xmlns="http://www.w3.org/2000/svg"
  viewBox="0 0 420 160"
  className="h-10 text-white"   // change to text-purple-600 etc.
  role="img"
  aria-label="BNM · Baainearme"
>
  <g fill="none" stroke="currentColor" strokeWidth="12" strokeLinecap="round">
    {/* Left ring */}
    <circle cx="50" cy="60" r="40" />
    {/* Vertical divider */}
    <line x1="110" y1="20" x2="110" y2="120" />
  </g>

  {/* Text block */}
  <g fill="currentColor">
    {/* BNM */}
    <text
      x="140"
      y="72"
      fontFamily="Poppins, Inter, Arial, sans-serif"
      fontWeight="800"
      fontSize="56"
    >
      BNM
    </text>

    {/* Baainearme */}
    <text
      x="140"
      y="120"
      fontFamily="Poppins, Inter, Arial, sans-serif"
      fontWeight="700"
      fontSize="32"
    >
      Baainearme
    </text>
  </g>
</svg>

              <p className="text-gray-400">
                Your questions answered, your problems solved
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">For Customers</h3>
              <ul className="space-y-2 text-gray-400">
               
                <li><Link to="/customer-support" className="hover:text-white cursor-pointer">Customer Support</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/partner" className="hover:text-white cursor-pointer">Become Partner</Link></li>
                <li><Link to="/about" className="hover:text-white cursor-pointer">About Us</Link></li>
                <li><Link to="/faqs" className="hover:text-white cursor-pointer">FAQs</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/customer-terms" className="hover:text-white cursor-pointer">Terms & Conditions</Link></li>
                <li><Link to="/customer-privacy" className="hover:text-white cursor-pointer">Privacy Policy</Link></li>
                <li><Link to="/refund-policy" className="hover:text-white cursor-pointer">Refund Policy</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Baainearme. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
